/**
 * Composable for managing Algolia infinite hits functionality.
 * Provides methods and references for handling infinite scrolling and pagination.
 *
 * @returns {Object} An object containing methods and reactive references for infinite hits management
 * @property {Function} refineAndUpdateState - Updates state and hit count when loading more items
 * @property {ComputedRef<string>} previousCanonicalPage - Generates the canonical URL for the previous page
 * @property {ComputedRef<string>} nextCanonicalPage - Generates the canonical URL for the next page
 * @property {Function} setInfiniteScroll - Configures infinite scroll using intersection observer
 * @property {Function} setInfiniteScroll2Page - Alternative method to trigger infinite scroll
 * @property {Ref<boolean>} infScroll - Indicates if infinite scroll is active
 * @property {Ref<any>} state - Algolia widget state containing hits
 * @property {Ref<HTMLElement | null>} loadMoreButton - Reference to the load more button
 */
export const useAlgoliaAsInfiniteHits = () => {
  const { state } = useAisWidget('infiniteHits')
  const isLoading = useLoaderState()
  const infScroll = ref<boolean>(false)
  const route = useRoute()
  const hitsPerPage = ref(24)
  const myHits = ref(24)
  const targetInfiniteScroll = ref<HTMLElement | null>(null)
  const clickCounter = ref(Number(route.query.page))
  const loadMoreButton = ref<HTMLElement | null>(null)

  /**
   * Refines and updates the state when loading more items.
   *
   * @param {Function} rNext - Function to load next set of items
   * @param {boolean} [prev=false] - Flag to indicate if loading previous page
   */
  const refineAndUpdateState = (rNext: any, prev?: boolean) => {
    isLoading.value = true
    rNext()
    myHits.value = myHits.value + hitsPerPage.value
    if (prev) clickCounter.value = clickCounter.value - 1
  }

  /**
   * Computes the canonical URL for the previous page.
   *
   * @returns {string} Canonical URL for the previous page
   */
  const previousCanonicalPage = computed(() => {
    if (clickCounter.value > 2) {
      return `${route.path}?page=${clickCounter.value - 1}`
    } else {
      return `${route.path}`
    }
  })

  /**
   * Computes the canonical URL for the next page.
   *
   * @returns {string} Canonical URL for the next page
   */
  const nextCanonicalPage = computed(() => {
    if (route.query.page) {
      return `${route.path}?page=${Number(route.query.page) + 1}`
    } else {
      return `${route.path}?page=2`
    }
  })

  /**
   * Sets up infinite scroll using Intersection Observer.
   * Triggers load more button when the target element is intersecting.
   */
  const setInfiniteScroll = () => {
    targetInfiniteScroll.value = document.getElementById('loadMoreStrip')
    infScroll.value = true
    useIntersectionObserver(
      targetInfiniteScroll,
      ([{ isIntersecting }]) => {
        if (isIntersecting) {
          loadMoreButton.value?.click()
        }
      },
      { threshold: 0 }
    )
  }

  /**
   * Alternative method to trigger infinite scroll for the second page.
   * Directly clicks the load more button.
   */
  const setInfiniteScroll2Page = () => {
    targetInfiniteScroll.value = document.getElementById('loadMoreStrip')
    infScroll.value = true
    loadMoreButton.value?.click()
  }

  // Watcher that is used by Armani Loader when new hits are loaded
  watch(
    () => state.value.hits,
    () => {
      isLoading.value = false
    }
  )

  return {
    refineAndUpdateState,
    previousCanonicalPage,
    nextCanonicalPage,
    setInfiniteScroll,
    setInfiniteScroll2Page,
    infScroll,
    state,
    loadMoreButton,
  }
}
